import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Clock, Trophy, Activity } from 'lucide-react';
import SkinCard from '../components/SkinCard';
import CategoryFilter from '../components/CategoryFilter';
import { skinRankingsAPI } from '../services/api';
import toast from 'react-hot-toast';

const HomePage = () => {
  const [rankings, setRankings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recentChanges, setRecentChanges] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [changesLoading, setChangesLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadCategories();
    loadStatistics();
  }, []);

  useEffect(() => {
    loadRankings();
  }, [activeCategory]);

  const loadCategories = async () => {
    try {
      const response = await skinRankingsAPI.getCategories();
      const categoriesData = response.data.data;
      // Convert to array format for CategoryFilter component
      const categoriesArray = Object.entries(categoriesData).map(([key, description]) => ({
        id: key.toLowerCase(),
        name: key,
        description: description
      }));
      
      // Add "All Categories" option at the beginning
      const categoriesWithAll = [
        { id: 'all', name: 'All Categories', description: 'View all skins across all categories' },
        ...categoriesArray
      ];
      
      setCategories(categoriesWithAll);
    } catch (error) {
      toast.error('Failed to load categories');
      console.error('Error loading categories:', error);
    }
  };

  const loadRankings = async () => {
    setLoading(true);
    try {
      const category = activeCategory === 'all' ? null : categories.find(c => c.id === activeCategory)?.name;
      const response = await skinRankingsAPI.getTopSkins(100, category);
      setRankings(response.data.data);
    } catch (error) {
      toast.error('Failed to load rankings');
      console.error('Error loading rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await skinRankingsAPI.getRankingStatistics();
      setStats(response.data.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadRecentChanges = async () => {
    setChangesLoading(false);
    // For now, simulate recent changes since we don't have change tracking yet
    // In a future update, we could track ranking position changes
    setRecentChanges([]);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  // Transform ranking data to match SkinCard expectations
  const transformSkinData = (rankingData) => {
    return rankingData.map((skin, index) => ({
      id: skin.id, // Add skin ID for navigation
      rank: skin.category_rank || skin.rank_position || index + 1, // Use category rank if available, otherwise global rank
      skin_name: `${skin.weapon_name} | ${skin.skin_name}`,
      weapon_name: skin.weapon_name,
      category: skin.category,
      rarity: 'Unknown', // We don't have rarity data yet
      image_url: skin.image_url,
      usage_count: skin.usage_count,
      unique_players: Math.ceil(skin.usage_count * 0.6), // Estimate players from usage
      popularity_score: skin.total_score?.toFixed(2) || '0.00',
      avg_price: null, // No price data yet
      rank_change: null // No change tracking yet
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
      >
        <h1 className="text-4xl md:text-6xl font-bold gradient-text mb-4">
          CS2 Skins Rankings
        </h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Discover the most popular CS2 skins based on professional player usage and community preferences
        </p>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <div className="card p-6 text-center">
          <Activity className="h-10 w-10 text-primary-400 mx-auto mb-3" />
          <div className="text-2xl font-bold text-white mb-1">
            {stats ? stats.total_skins : '-'}
          </div>
          <div className="text-sm text-gray-400">Total Tracked Skins</div>
        </div>
        <div className="card p-6 text-center">
          <Clock className="h-10 w-10 text-blue-400 mx-auto mb-3" />
          <div className="text-2xl font-bold text-white mb-1">
            {stats?.top_skin ? Math.round(stats.top_skin.score) : '-'}
          </div>
          <div className="text-sm text-gray-400">Highest Score</div>
        </div>
      </motion.div>


      {/* Category Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          loading={!categories.length}
        />
      </motion.div>

      {/* Rankings Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            Top 100 {activeCategory === 'all' ? 'Skins' : categories.find(c => c.id === activeCategory)?.name || 'Skins'}
          </h2>
          <div className="text-sm text-gray-400">
            {rankings.length} skins found
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="aspect-video bg-gray-700 rounded-lg mb-4" />
                <div className="space-y-2">
                  <div className="h-4 bg-gray-700 rounded w-3/4" />
                  <div className="h-3 bg-gray-700 rounded w-1/2" />
                  <div className="h-3 bg-gray-700 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {transformSkinData(rankings).map((skin, index) => (
              <motion.div
                key={skin.id} // Use skin ID as key for stability
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <SkinCard skin={skin} />
              </motion.div>
            ))}
          </div>
        )}

        {!loading && rankings.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No skins found</h3>
            <p className="text-gray-500">Try processing some demo files first or select a different category</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default HomePage;
